/**
 * The condition grammar's fact names (engine.html §3 "Conditions: what can be read, and where"): 12 namespaces and the 6 raid facts. A
 * port of shared-cs `Story/Conditions.cs` (`StoryConditions`, S1-ENG): the same fixed facts with their value types and argument counts,
 * plus the dynamic `var.*`, `flag.*` and `cast.<role>.<prop>` forms. The picker offers these instead of free text.
 */
export type FactValueType = 'bool' | 'int' | 'string';
/** Where a fact is evaluated: folded by the server at issue, rolled into the ticket, read on the device at a choice, or the path. */
export type FactWhere = 'server' | 'ticket' | 'raid' | 'path';

export type FactSpec = {
  name: string;
  type: FactValueType;
  minArgs: number;
  maxArgs: number;
  where: FactWhere;
  /** Argument names for the picker, in order. */
  args?: string[];
};

const f = (
  name: string,
  type: FactValueType,
  minArgs: number,
  maxArgs: number,
  where: FactWhere,
  args?: string[],
): FactSpec => ({ name, type, minArgs, maxArgs, where, args });

/** shared-cs `StoryConditions.Fixed`. */
export const FIXED_FACTS: FactSpec[] = [
  f('team.size', 'int', 0, 0, 'server'),
  f('team.bestStat', 'int', 1, 1, 'server', ['stat']),
  f('member.stat', 'int', 2, 2, 'server', ['role | player | leader', 'stat']),
  f('team.leaderIsPlayer', 'bool', 0, 0, 'server'),
  f('run.day', 'int', 0, 0, 'server'),
  f('time.localHour', 'int', 0, 0, 'server'),
  f('time.isNight', 'bool', 0, 0, 'server'),
  f('building.category', 'string', 0, 0, 'server'),
  f('building.kind', 'string', 0, 0, 'server'),
  f('encounter.kind', 'string', 0, 0, 'server'),
  f('ledger.chose', 'bool', 3, 4, 'server', ['chain', 'beat', 'option', 'scope:account?']),
  f('ledger.outcome', 'bool', 1, 3, 'server', ['outcomeId', '…', '…']),
  f('ledger.tag', 'bool', 1, 2, 'server', ['tag', 'scope:account?']),
  f('ledger.count', 'int', 1, 2, 'server', ['tag', 'scope:account?']),
  f('ledger.raidsSince', 'int', 1, 2, 'server', ['tag', 'scope:account?']),
  f('ledger.truth', 'string', 2, 3, 'server', ['chain', 'var', 'scope:account?']),
  f('ally.active', 'bool', 1, 1, 'server', ['allyId']),
  f('ally.count', 'int', 0, 0, 'server'),
  f('ally.disposition', 'string', 1, 1, 'server', ['allyId']),
  f('ally.deliveriesLeft', 'int', 1, 1, 'server', ['allyId']),
  // Day Zero (ADR 0074): a signed ticket var holds a value (a comma list's element counts); folded at issue like a server fact.
  f('ticket.varHas', 'bool', 2, 2, 'server', ['key', 'value']),
  f('path.chose', 'bool', 2, 2, 'path', ['beat', 'option']),
  f('raid.squadAlive', 'int', 0, 0, 'raid'),
  f('raid.leaderHpPct', 'int', 0, 0, 'raid'),
  f('raid.hordeLevel', 'int', 0, 0, 'raid'),
  f('raid.elapsedS', 'int', 0, 0, 'raid'),
  f('raid.carrying', 'int', 1, 1, 'raid', ['itemId or tag:<tag>']),
  f('raid.objectiveDone', 'bool', 1, 1, 'raid', ['objectiveId']),
  // Card #309: the player's own body saw the trigger point (false when a teammate set it off unseen).
  f('raid.playerPresent', 'bool', 0, 0, 'raid'),
];

/** `StoryConditions.CastProps`: `cast.<role>.<prop>`. */
export const CAST_PROPS: Record<string, FactValueType> = {
  condition: 'string',
  hpPct: 'int',
  bound: 'bool',
  name: 'string',
};

/** `StoryConditions.AccountScopeArg`. */
export const ACCOUNT_SCOPE_ARG = 'scope:account';

export type Namespace = {
  id: string;
  label: string;
  facts: FactSpec[];
  /** Dynamic names: the suffix after the prefix comes from the chain (`var.companion.bitten`) or the content (`flag.npc.okafor`). */
  dynamic?: { prefix: string; hint: string };
};

const ns = (id: string, label: string, dynamic?: Namespace['dynamic']): Namespace => ({
  id,
  label,
  dynamic,
  facts: FIXED_FACTS.filter((x) => namespaceIdOf(x.name) === id),
});

/** `StoryConditions.NamespaceOf`: the text before the first dot; `encounter` reads as `building`. */
export function namespaceIdOf(fact: string | null | undefined): string {
  if (!fact) return '';
  const dot = fact.indexOf('.');
  const head = dot < 0 ? fact : fact.slice(0, dot);
  return head === 'encounter' ? 'building' : head;
}

/** The 12 namespaces (`StoryConditions.Namespaces`), for the picker. */
export const NAMESPACES: Namespace[] = [
  ns('var', 'var.* (hidden vars and tells)', { prefix: 'var.', hint: 'a hidden var or tell id' }),
  ns('cast', 'cast.* (the bound roles)', {
    prefix: 'cast.',
    hint: '<role>.condition|hpPct|bound|name',
  }),
  ns('member', 'member.stat (effective stats)'),
  ns('team', 'team.*'),
  ns('run', 'run.*'),
  ns('time', 'time.*'),
  ns('building', 'building.* / encounter.*'),
  ns('ledger', 'ledger.* (server only)'),
  ns('flag', 'flag.* (world flags)', { prefix: 'flag.', hint: 'a world flag key (npc.okafor)' }),
  ns('ally', 'ally.*'),
  ns('path', 'path.* (earlier choices in this chain)'),
  ns('raid', 'raid.* (on the device, at a choice)'),
  ns('ticket', 'ticket.varHas (signed ticket vars, Day Zero)'),
];

/** `StoryConditions.MemberStat` / `TeamBestStat` (card 19): the facts that read an effective stat. */
export const STAT_FACTS = ['member.stat', 'team.bestStat'] as const;

/** `StoryConditions.BuiltInRoles`: the roles `member.stat` reads besides the chain's cast. */
export const STAT_BUILT_IN_ROLES = ['player', 'leader'] as const;

/** The closed list of raid facts (`StoryConditions.RaidFacts`, with the prefix). */
export const RAID_FACTS = FIXED_FACTS.filter((x) => x.where === 'raid').map((x) => x.name);

/** `StoryConditions.Spec`: the spec for a fact name, or undefined when the grammar doesn't know it. */
export function factSpec(fact: string | null | undefined): FactSpec | undefined {
  if (!fact) return undefined;
  const fixed = FIXED_FACTS.find((x) => x.name === fact);
  if (fixed) return fixed;
  const n = namespaceIdOf(fact);
  if (n === 'var' && fact.length > 4) return f(fact, 'string', 0, 0, 'ticket');
  if (n === 'flag' && fact.length > 5) return f(fact, 'string', 0, 0, 'server');
  if (n === 'cast') {
    const parts = fact.split('.');
    const type = parts.length === 3 && parts[1]!.length > 0 ? CAST_PROPS[parts[2]!] : undefined;
    if (type) return f(fact, type, 0, 0, 'server');
  }
  return undefined;
}

/** The namespace a fact belongs to, or undefined when unknown. */
export function namespaceOf(fact: string): Namespace | undefined {
  return NAMESPACES.find((n) => n.id === namespaceIdOf(fact));
}

/**
 * The scenario layer's condition facts (shared-cs `ScenarioConditions.IsKnownFact`, S1-SCN): signed vars, objective, actor, found and
 * timer states, and `raid.innocents_killed`. Scenario trigger `if`s use these instead of the engine's namespaces.
 */
export const SCENARIO_NAMESPACES: Namespace[] = [
  {
    id: 'var',
    label: 'var.* (signed vars, flag snapshots)',
    facts: [],
    dynamic: { prefix: 'var.', hint: 'hidden.okafor.bitten' },
  },
  {
    id: 'objective',
    label: 'objective.*',
    facts: [],
    dynamic: { prefix: 'objective.', hint: 'an objective id' },
  },
  {
    id: 'actor',
    label: 'actor.*',
    facts: [],
    dynamic: { prefix: 'actor.', hint: '<id> or <id>.health' },
  },
  {
    id: 'found',
    label: 'found.*',
    facts: [],
    dynamic: { prefix: 'found.', hint: 'a target (actor:okafor)' },
  },
  { id: 'timer', label: 'timer.*', facts: [], dynamic: { prefix: 'timer.', hint: 'a timer id' } },
  {
    id: 'raid',
    label: 'raid.innocents_killed',
    facts: [f('raid.innocents_killed', 'int', 0, 0, 'raid')],
  },
];
